import React, { useMemo, useState } from 'react'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'

interface SortableRepeaterProps<T extends { id: string }> {
  items: T[]
  onChange: (items: T[]) => void
  renderItem: (item: T, index: number) => React.ReactNode
  getDragLabel?: (item: T) => string
  emptyMessage?: string
  className?: string
}

function SortableRepeater<T extends { id: string }>({
  items,
  onChange,
  renderItem,
  getDragLabel,
  emptyMessage = 'No items added yet.',
  className = '',
}: SortableRepeaterProps<T>) {
  const [activeId, setActiveId] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  )

  const sortableIds = useMemo(() => items.map((item) => item.id), [items])

  const activeItem = activeId
    ? items.find((item) => item.id === activeId)
    : null

  return (
    <div className={className}>
      {items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-600 dark:text-slate-400">
          {emptyMessage}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragStart={(event) => setActiveId(String(event.active.id))}
          onDragEnd={(event) => {
            const { active, over } = event
            setActiveId(null)

            if (!over || active.id === over.id) return

            const oldIndex = items.findIndex((item) => item.id === active.id)
            const newIndex = items.findIndex((item) => item.id === over.id)

            if (oldIndex !== -1 && newIndex !== -1) {
              onChange(arrayMove(items, oldIndex, newIndex))
            }
          }}
          onDragCancel={() => setActiveId(null)}
        >
          <SortableContext
            items={sortableIds}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {items.map((item, index) =>
                renderItem(item, index)
              )}
            </div>
          </SortableContext>

          <DragOverlay>
            {activeItem ? (
              <div className="rounded-lg border border-blue-200 bg-white px-4 py-3 shadow-lg dark:border-blue-700 dark:bg-slate-800">
                <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                  {getDragLabel?.(activeItem) ?? 'Moving item'}
                </p>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

    </div>
  )
}

export default SortableRepeater
